import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { parseSellerReviewFeedback } from "@/lib/seller-review-feedback";
import type { Item, ItemCategory, ItemStatus, Coordinates } from "@/lib/types";

type ServiceSupabase = SupabaseClient<Database>;

type HomepageItemRow = Pick<
  Database["public"]["Tables"]["items"]["Row"],
  | "id"
  | "title"
  | "description"
  | "defects"
  | "price"
  | "quantity"
  | "category"
  | "subcategory"
  | "location"
  | "coordinates"
  | "seller_id"
  | "status"
  | "seller_review_feedback"
  | "created_at"
  | "updated_at"
>;

const HOMEPAGE_ITEM_COLUMNS =
  "id,title,description,defects,price,quantity,category,subcategory,location,coordinates,seller_id,status,seller_review_feedback,created_at,updated_at";

/** SIO platform sales only — excludes legacy `sold` and `sold_elsewhere`. */
export const HOMEPAGE_RECENTLY_SOLD_STATUSES = ["sale_complete"] as const;

const VALID_CATEGORIES: readonly ItemCategory[] = [
  "baby",
  "bedding",
  "decor",
  "digital_wears",
  "electronics",
  "fashion",
  "fitness",
  "furniture",
  "gaming",
  "kitchen",
  "lighting",
  "outdoor",
  "personal_computers",
  "phones_tabs",
  "power_solutions",
  "tools",
  "other",
];

const POWER_SOLUTIONS_SUBCATEGORY_IDS = new Set([
  "generator",
  "inverter",
  "inverter-battery",
]);

function toItemCategory(
  category: string | null,
  subcategory?: string | null,
): ItemCategory {
  if (
    category === "electronics" &&
    subcategory &&
    POWER_SOLUTIONS_SUBCATEGORY_IDS.has(subcategory)
  ) {
    return "power_solutions";
  }
  const resolved = (category || "other") as ItemCategory;
  return VALID_CATEGORIES.includes(resolved) ? resolved : "other";
}

function toItemStatus(status: string | null): ItemStatus {
  return (status || "new_submission") as ItemStatus;
}

function toCoordinates(
  value: HomepageItemRow["coordinates"],
): Coordinates | undefined {
  if (!value) return undefined;
  const coords = value as { lat?: unknown; lng?: unknown };
  if (typeof coords.lat === "number" && typeof coords.lng === "number") {
    return { lat: coords.lat, lng: coords.lng };
  }
  return undefined;
}

/**
 * Server-side Recently Sold fetch (service role bypasses RLS for public homepage preview).
 */
export async function fetchHomepageRecentlySoldItems(
  service: ServiceSupabase,
  limit: number,
): Promise<Item[]> {
  const safeLimit = Math.min(Math.max(limit, 1), 50);

  const { data, error } = await service
    .from("items")
    .select(HOMEPAGE_ITEM_COLUMNS)
    .in("status", [...HOMEPAGE_RECENTLY_SOLD_STATUSES])
    .order("updated_at", { ascending: false })
    .limit(safeLimit)
    .returns<HomepageItemRow[]>();

  if (error) {
    throw new Error(error.message);
  }

  if (!data?.length) {
    return [];
  }

  const itemIds = data.map((row) => row.id).filter(Boolean);
  const imagesByItemId = new Map<string, string[]>();

  if (itemIds.length > 0) {
    const { data: imageRows, error: imageError } = await service
      .from("item_images")
      .select("item_id, url, position, is_main")
      .in("item_id", itemIds)
      .order("position", { ascending: true });

    if (imageError) {
      throw new Error(imageError.message);
    }

    for (const row of imageRows ?? []) {
      const urls = imagesByItemId.get(row.item_id) ?? [];
      urls.push(row.url);
      imagesByItemId.set(row.item_id, urls);
    }
  }

  return data
    .filter((row) => row.id && row.title && row.seller_id)
    .map((row) => ({
      id: row.id,
      title: row.title,
      description: row.description || undefined,
      defects: row.defects || undefined,
      price: row.price,
      quantity: row.quantity || 1,
      category: toItemCategory(row.category, row.subcategory),
      subcategory: row.subcategory || "",
      images: imagesByItemId.get(row.id) ?? [],
      location: row.location || "Lagos",
      coordinates: toCoordinates(row.coordinates),
      sellerId: row.seller_id,
      status: toItemStatus(row.status),
      sellerReviewFeedback: parseSellerReviewFeedback(
        row.seller_review_feedback,
      ),
      createdAt: row.created_at ? new Date(row.created_at) : new Date(),
      updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
    }));
}

/** JSON-safe shape for `/api/items/recently-sold`. */
export type HomepageRecentlySoldItemDto = Omit<
  Item,
  "createdAt" | "updatedAt" | "imageDetails"
> & {
  createdAt: string;
  updatedAt: string;
};

export function serializeHomepageRecentlySoldItems(
  items: Item[],
): HomepageRecentlySoldItemDto[] {
  return items.map(({ createdAt, updatedAt, ...rest }) => ({
    ...rest,
    createdAt: createdAt.toISOString(),
    updatedAt: updatedAt.toISOString(),
  }));
}

export function deserializeHomepageRecentlySoldItems(
  items: HomepageRecentlySoldItemDto[],
): Item[] {
  return items.map(({ createdAt, updatedAt, ...rest }) => ({
    ...rest,
    createdAt: new Date(createdAt),
    updatedAt: new Date(updatedAt),
  }));
}
